import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../main/Apicall";
import ToastPopup from "../Popups/ToastPopup";
import "../css/others/Card.css";
import { AuthConfig, ProviderConfig, ClientOption, ProviderData, SamlMetadata, SamlSpDetails, createToastMessage, ToastMessage } from "../Interfaces/Interface";
const providerConfigs: { [key: string]: ProviderConfig } = {
  google: { idpClientId: "", idpClientSecret: "", redirectUri: "" },
  microsoft: { idpClientId: "", idpClientSecret: "", tenantId: "", redirectUri: "" },
  auth0: { idpClientId: "", idpClientSecret: "", auth0Domain: "", redirectUri: "" },
  "azure-saml": { idpClientId: "", idpClientSecret: "", redirectUri: "" }
};
const IdpConfiguration: React.FC = () => {
  const [formData, setFormData] = useState<AuthConfig>({ clientId: "", provider: "", idpClientId: "", idpClientSecret: "", tenantId: "", auth0Domain: "", redirectUri: "" });
  const [providers, setProviders] = useState<ProviderData[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [selectedProviderId, setSelectedProviderId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [samlFile, setSamlFile] = useState<File | null>(null);
  const [isSamlUploading, setIsSamlUploading] = useState<boolean>(false);
  const [showSamlUpload, setShowSamlUpload] = useState<boolean>(false);
  const [samlMetadata, setSamlMetadata] = useState<SamlMetadata>({ idpEntityId: "", idpSsoUrl: "", idpX509Certificate: "" });
  const [samlSpDetails, setSamlSpDetails] = useState<SamlSpDetails>({ spEntityId: "", acsUrl: "" });
  const [, setSelectedClientSubdomain] = useState<string>("");
  const [isParsingSaml, setIsParsingSaml] = useState<boolean>(false);
  const navigate = useNavigate();
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await apiCall("get", "/api/providers/names");
        const formattedProviders = response.map((p: any) => ({ value: p.providerId, label: p.providerName, providerId: p.providerId, providerName: p.providerName }));
        setProviders(formattedProviders);
      } catch (error) {
        console.error("Error fetching providers:", error);
      }
    };
    const fetchClients = async () => {
      try {
        const response = await apiCall("get", "/api/clients/names/all");
        const clientData = response.map((client: any) => ({ clientId: client.clientId, clientName: `${client.clientName} (${client.clientId})`, subdomain: client.subdomain || "" }));
        setClients(clientData);
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };
    fetchProviders();
    fetchClients();
  }, []);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "clientId") {
      const selectedClient = clients.find(client => client.clientId === value);
      if (selectedClient) {
        const subdomain = selectedClient.subdomain || "";
        setSelectedClientSubdomain(subdomain);
        if (subdomain && subdomain.trim() !== "") {
          const spEntityId = `https://${subdomain}/api/saml/metadata`;
          const acsUrl = `https://${subdomain}/api/saml/acs`;
          setSamlSpDetails({ spEntityId, acsUrl });
        } else {
          setSamlSpDetails({ spEntityId: "N/A", acsUrl: "N/A" });
        }
      }
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleProviderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const providerId = e.target.value;
    const selected = providers.find(p => p.value === providerId);
    const providerName = selected?.providerName || "";
    setSelectedProvider(providerName);
    setSelectedProviderId(providerId);
    const providerConfig = providerConfigs[providerName.toLowerCase()] || {};
    setFormData((prev) => ({ clientId: prev.clientId, provider: providerName, idpClientId: providerConfig.idpClientId || "", idpClientSecret: providerConfig.idpClientSecret || "", tenantId: providerConfig.tenantId || "", auth0Domain: providerConfig.auth0Domain || "", redirectUri: providerConfig.redirectUri || "" }));
    setSamlFile(null);
    setSamlMetadata({ idpEntityId: "", idpSsoUrl: "", idpX509Certificate: "" });
    const isSamlProvider = providerName.toLowerCase().includes("saml")
    setShowSamlUpload(isSamlProvider);
  };
  const parseSamlMetadata = (xmlContent: string): SamlMetadata => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlContent, "text/xml");
    let idpEntityId = "";
    let idpSsoUrl = "";
    let idpX509Certificate = "";
    const entityDescriptor = xmlDoc.getElementsByTagName("EntityDescriptor")[0];
    if (entityDescriptor) {
      idpEntityId = entityDescriptor.getAttribute("entityID") || "";
    }
    const ssoNodes = xmlDoc.getElementsByTagName("SingleSignOnService");
    if (ssoNodes.length > 0) {
      for (let i = 0; i < ssoNodes.length; i++) {
        const binding = ssoNodes[i].getAttribute("Binding");
        if (
          binding &&
          (binding.includes("HTTP-Redirect") || binding.includes("HTTP-POST"))
        ) {
          idpSsoUrl = ssoNodes[i].getAttribute("Location") || "";
          break;
        }
      }
      if (!idpSsoUrl && ssoNodes[0]) {
        idpSsoUrl = ssoNodes[0].getAttribute("Location") || "";
      }
    }
    const keyDescriptor = xmlDoc.getElementsByTagName("KeyDescriptor")[0];
    if (keyDescriptor) {
      const x509Data = keyDescriptor.getElementsByTagName("X509Certificate")[0];
      if (x509Data && x509Data.textContent) {
        const cert = x509Data.textContent
          .replace(/\s+/g, '')
          .replace(/(.{64})/g, '$1\n')
          .trim();
        idpX509Certificate = `-----BEGIN CERTIFICATE-----\n${cert}\n-----END CERTIFICATE-----`;
      }
    }
    if (!idpEntityId) {
      const entityIdElement = xmlDoc.getElementsByTagName("EntityID")[0];
      if (entityIdElement && entityIdElement.textContent) {
        idpEntityId = entityIdElement.textContent;
      }
    }
    if (!idpSsoUrl) {
      const ssoUrlElement = xmlDoc.getElementsByTagName("Location")[0];
      if (ssoUrlElement && ssoUrlElement.textContent) {
        idpSsoUrl = ssoUrlElement.textContent;
      }
    }
    return {
      idpEntityId, idpSsoUrl, idpX509Certificate
    };
  };
  const handleSamlFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xml')) {
      setMessage(createToastMessage("Please upload an XML file (.xml extension)", "error", 3000));
      return;
    }
    setSamlFile(file);
    setIsParsingSaml(true);
    setMessage(null);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const xmlContent = event.target?.result as string;
        const metadata = parseSamlMetadata(xmlContent);
        setSamlMetadata(metadata);
        if (metadata.idpEntityId || metadata.idpSsoUrl || metadata.idpX509Certificate) {
          setMessage(createToastMessage("SAML metadata extracted successfully!", "success", 3000));
        } else {
          setMessage(createToastMessage("SAML file uploaded but no metadata could be extracted. Please check the file format.", "warning", 3000));
        }
      } catch (error) {
        console.error("Error parsing SAML file:", error);
        setMessage(createToastMessage("Error parsing SAML XML file. Please check the file format.", "error", 3000));
      } finally {
        setIsParsingSaml(false);
      }
    };
    reader.onerror = () => {
      setMessage(createToastMessage("Error reading the XML file", "error", 3000));
      setIsParsingSaml(false);
    };
    reader.readAsText(file);
  };
  const handleSamlSubmit = async () => {
    if (!formData.clientId) {
      setMessage(createToastMessage("Please select a client first", "warning", 3000));
      return;
    }
    if (!selectedProviderId) {
      setMessage(createToastMessage("Please select a provider", "warning", 3000));
      return;
    }
    if (!samlFile) {
      setMessage(createToastMessage("Please select a SAML XML file to upload", "warning", 3000));
      return;
    }
    if (!samlMetadata.idpEntityId || !samlMetadata.idpSsoUrl) {
      setMessage(createToastMessage("Please ensure the SAML file contains Entity ID and SSO URL", "warning", 3000));
      return;
    }
    if (samlSpDetails.spEntityId === "N/A" || samlSpDetails.acsUrl === "N/A") {
      setMessage(createToastMessage("Cannot configure SAML: Client subdomain is not available. Please update client configuration with a valid subdomain.", "error", 3000));
      return;
    }
    if (!samlSpDetails.spEntityId || !samlSpDetails.acsUrl) {
      setMessage(createToastMessage("SP Entity ID and ACS URL are not available. Please ensure the client has a valid subdomain configured.", "warning", 3000));
      return;
    }
    setIsLoading(true);
    setIsSamlUploading(true);
    setError(null);
    try {
      const payload = { idpEntityId: samlMetadata.idpEntityId, idpSsoUrl: samlMetadata.idpSsoUrl, idpX509Certificate: samlMetadata.idpX509Certificate, spEntityId: samlSpDetails.spEntityId, acsUrl: samlSpDetails.acsUrl };
      const response = await apiCall("post", `/api/idp-config/saml-azure?clientId=${encodeURIComponent(formData.clientId)}&provider=${encodeURIComponent(selectedProviderId)}`, payload);
      if (response) {
        setMessage(createToastMessage("SAML configuration uploaded successfully!", "success", 3000));
        setTimeout(() => {
          navigate("/client");
        }, 3000);
      }
    } catch (error: any) {
      console.error("SAML processing error:", error);
      setMessage(createToastMessage(error.response?.data?.message || error.message || "Failed to process SAML configuration", "error", 3000));
    } finally {
      setIsLoading(false);
      setIsSamlUploading(false);
    }
  };
  const handleBackClick = () => {
    navigate(-1);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    if (showSamlUpload) {
      setMessage(createToastMessage("Please use the SAML XML upload functionality for SAML/Azure providers", "warning", 3000));
      setIsLoading(false);
      return;
    }
    if (!selectedProviderId) {
      setMessage(createToastMessage("Please select a provider.", "warning", 3000));
      setIsLoading(false);
      return;
    }
    if (!formData.idpClientId || !formData.idpClientSecret || !formData.redirectUri) {
      setMessage(createToastMessage("All fields are required for the selected provider.", "warning", 3000));
      setIsLoading(false);
      return;
    }
    const payload: any = { clientId: formData.clientId, provider: selectedProviderId, idpClientId: formData.idpClientId, encryptedIdpClientSecret: formData.idpClientSecret, redirectUri: formData.redirectUri };
    if (selectedProvider === "microsoft" && formData.tenantId) {
      payload.tenantId = formData.tenantId;
    }
    if (selectedProvider === "auth0" && formData.auth0Domain) {
      payload.auth0Domain = formData.auth0Domain;
    }
    try {
      const response = await apiCall("post", "/api/idp-config", payload);
      if (response) {
        setMessage(createToastMessage("Configuration submitted successfully!", "success", 3000));
        setTimeout(() => { navigate("/client"); }, 3000);
      }
    } catch (error: any) {
      console.error("Submission error:", error);
      setMessage(createToastMessage(error.response?.data?.message || error.message || "Failed to submit configuration. Please try again.", "error", 3000));
    } finally {
      setIsLoading(false);
    }
  };
  if (error) {
    return (
      <div className="container">
        <div className="error-message">{error}</div>
        <button className="back-button" onClick={handleBackClick}>Back</button>
      </div>
    );
  }
  const handleBrowseClick = () => {
    const input = document.getElementById('saml-file-upload');
    if (input) input.click();
  };
  return (
    <div className="container">
      <h2 className="titlee">IDP Configuration</h2>
      <nav className="card-full">
        <nav className="card-inner-full">
          <label className="label">Select a Client ID:</label>
          <select name="clientId" value={formData.clientId} onChange={handleChange} className="full-width" disabled={isLoading}>
            <option value="" disabled>Select a Client</option>
            {clients.map((client) => (
              <option key={client.clientId} value={client.clientId}>{client.clientName}</option>
            ))}
          </select>
        </nav>
      </nav>
      <div className="card-container">
        <div className="grid">
          {formData.clientId && (
            <div className="card">
              <div className="card-inner">
                <label className="label">Provider:</label>
                <select name="provider" value={selectedProviderId} onChange={handleProviderChange} disabled={isLoading}>
                  <option value="" disabled>Select a provider</option>
                  {providers.map((provider) => (
                    <option key={provider.value} value={provider.value}>{provider.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {selectedProvider && !showSamlUpload && (
            <>
              <div className="card">
                <div className="card-inner">
                  <label className="label">IDP Client ID:</label>
                  <input name="idpClientId" type="text" placeholder="Enter IDP Client ID" value={formData.idpClientId} onChange={handleChange} disabled={isLoading} />
                </div>
              </div>
              <div className="card">
                <div className="card-inner">
                  <label className="label">IDP Client Secret:</label>
                  <input name="idpClientSecret" type="text" placeholder="Enter IDP Client Secret" value={formData.idpClientSecret} onChange={handleChange} disabled={isLoading} />
                </div>
              </div>
              <div className="card">
                <div className="card-inner">
                  <label className="label">Redirect URI:</label>
                  <input name="redirectUri" type="text" placeholder="Enter Redirect URI (e.g., https://yourdomain.com/callback)" value={formData.redirectUri || ""} onChange={handleChange} disabled={isLoading} />
                </div>
              </div>
              {selectedProvider === "microsoft" && (
                <div className="card">
                  <div className="card-inner">
                    <label className="label">Tenant ID:</label>
                    <input name="tenantId" type="text" placeholder="Enter Tenant ID" value={formData.tenantId || ""} onChange={handleChange} disabled={isLoading} />
                  </div>
                </div>
              )}
              {selectedProvider === "auth0" && (
                <div className="card">
                  <div className="card-inner">
                    <label className="label">Auth0 Domain:</label>
                    <input name="auth0Domain" type="text" placeholder="Enter Auth0 Domain" value={formData.auth0Domain || ""} onChange={handleChange} disabled={isLoading} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {showSamlUpload && formData.clientId && (
          <div className="grid mt-20">
            <div className="card">
              <div className="card-inner">
                <label className="label">SP Entity ID:</label>
                <div className={`readonly-value ${samlSpDetails.spEntityId === "N/A" ? "na-value" : ""}`}>
                  {samlSpDetails.spEntityId || "N/A"}
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-inner">
                <label className="label">ACS URL:</label>
                <div className={`readonly-value ${samlSpDetails.acsUrl === "N/A" ? "na-value" : ""}`}>
                  {samlSpDetails.acsUrl || "N/A"}
                </div>
              </div>
            </div>
          </div>
        )}
        {showSamlUpload && (
          <>
            <div className="card mt-20">
              <div className="card-inner">
                <label className="label">Upload SAML XML File:</label>
                <input type="file" accept=".xml" onChange={handleSamlFileChange} disabled={isSamlUploading || isLoading || isParsingSaml} className="file-input" id="saml-file-upload" />
                <label htmlFor="saml-file-upload" className="file-input-label">
                  {samlFile ? samlFile.name : "Choose XML File"}
                </label>
                <button className="browse-button" onClick={handleBrowseClick} disabled={isSamlUploading || isLoading || isParsingSaml}>Browse</button>
              </div>
            </div>
            {(samlMetadata.idpEntityId || samlMetadata.idpSsoUrl || samlMetadata.idpX509Certificate) && (
              <div className="grid mt-20">
                {samlMetadata.idpEntityId && (
                  <div className="card">
                    <div className="card-inner">
                      <label className="label">IDP Entity ID:</label>
                      <div className="readonly-value">{samlMetadata.idpEntityId}</div>
                    </div>
                  </div>
                )}
                {samlMetadata.idpSsoUrl && (
                  <div className="card">
                    <div className="card-inner">
                      <label className="label">SSO URL:</label>
                      <div className="readonly-value">{samlMetadata.idpSsoUrl}</div>
                    </div>
                  </div>
                )}
                {samlMetadata.idpX509Certificate && (
                  <div className="card">
                    <div className="card-inner">
                      <label className="label">X509 Certificate:</label>
                      <div className="readonly-value certificate-value">
                        <textarea readOnly value={samlMetadata.idpX509Certificate} className="certificate-textarea" rows={6} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <nav className="buttonss">
        <button className="back-button" onClick={handleBackClick} disabled={isLoading || isSamlUploading || isParsingSaml}>Back</button>
        {selectedProvider && !showSamlUpload && (
          <button className="submit-button" onClick={handleSubmit} disabled={isLoading || !formData.idpClientId || !formData.idpClientSecret || !formData.redirectUri}>{isLoading ? "Submitting..." : "Submit"}</button>
        )}
        {showSamlUpload && (
          <button className="submit-button" onClick={handleSamlSubmit} disabled={!samlFile || isLoading || isSamlUploading || isParsingSaml || !samlMetadata.idpEntityId || !samlMetadata.idpSsoUrl || samlSpDetails.spEntityId === "N/A" || samlSpDetails.acsUrl === "N/A"}>{isSamlUploading ? "Processing..." : "Upload & Configure SAML"}</button>
        )}
      </nav>
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default IdpConfiguration;